// ✅ Cleaned-up Pricing Page with Login Prompt Modal and Close Icon
import Link from "next/link";
import { useEffect, useState } from "react";
import { auth, db } from "../../../../lib/firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { useRouter } from "next/router";
import { getTrialStatus, startFreeTrial } from "../../../../lib/subscriptionUtils";
import { PRICING_PLANS } from "../../../../config/pricing";
import { CreditCard, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PricingSec({ disableAutoRedirect = false }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [user, setUser] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  // ✅ MOBILE SYNC: Enhanced state matching mobile subscription service
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        const userData = userDoc.data();
        setUser(userData);

        // ✅ MOBILE SYNC: Load trial status like mobile
        const trialData = await getTrialStatus(currentUser.uid, userData);
        setTrialStatus(trialData);

        // On the homepage we pass disableAutoRedirect so logged-in paid users
        // aren't bounced off the Discover homepage (index.js swaps them to the
        // feed once auth resolves instead).
        if (userData?.planId && userData.planId !== "free" && !disableAutoRedirect) {
          router.replace("/quester/dashboard");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Continue without user data if there's a permissions error
      }
    };
    fetchUser();
  }, []);

  const handleStartTrial = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    setLoading(true);

    // ✅ MOBILE SYNC: Use shared utility function
    const result = await startFreeTrial(currentUser.uid);
    
    if (result.success) {
      alert(`${result.title}\n\n${result.message}`);
      router.push("/quester/dashboard");
    } else {
      alert(`${result.title}\n\n${result.message}`);
    }
    
    setLoading(false);
  };

  const handleSubscribe = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    // Redirect to dashboard where users can subscribe using crypto payments
    router.push("/quester/dashboard");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" id="pricing">
      <h2 className="text-3xl md:text-5xl text-center font-black leading-tight text-gray-900 mb-4 uppercase tracking-tight ">
        {t("home.pricing.headingPrefix", "Simple, Transparent")} {" "}
        <span className="relative inline-block align-middle">
          <span
            className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-amber-500 rounded-full mx-4"
            style={{
              transform: "rotate(-1deg)",
              zIndex: 1,
              left: "-8px",
              right: "-8px",
              top: "-4px",
              bottom: "-4px",
            }}
          ></span>
          <span
            className="relative text-white px-5 py-1 font-extrabold z-10"
            style={{ fontFamily: "inherit", letterSpacing: "0.04em" }}
          >
            {t("home.pricing.headingHighlight", "Pricing")}
          </span>
        </span>
      </h2>

      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-500 rounded-lg p-6 mt-12 mb-16 text-center">
        <h2 className="text-xl font-semibold text-white flex items-center justify-center gap-2">
          <Gift className="w-5 h-5" />
          {t("home.pricing.freePlanAvailable", "Free Plan Available")}
        </h2>
        <p className="text-white mt-2">
          {t("home.pricing.freeMinutesNote", "Get 300 free minutes every month — forever!")}
          <br />
          <span className="inline-flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            {t("home.pricing.freePlanIncludes", "Free plan includes both audio and video calls. Upgrade anytime for more monthly minutes.")}
          </span>
          <br />
          {t("home.pricing.billedNote", "Calls are still billed at each Maven's listed per-minute rate.")}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        {PRICING_PLANS.map((plan, idx) => (
          <div
            key={idx}
            className={`flex flex-col transition-all duration-300 transform rounded-2xl shadow-md px-6 py-8 border
              ${plan.popular
                ? 'bg-gradient-to-b from-emerald-600 to-teal-600 border-emerald-600 hover:scale-105'
                : 'bg-gradient-to-b from-cream-100 to-cream-200 border-cream-300 hover:from-cream-100 hover:to-cream-300 hover:border-emerald-300 hover:scale-105'}
            `}
          >
            <div>
              <p className={`text-lg font-medium mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>{plan.name}</p>
              <h4 className={`text-3xl font-semibold mb-1 ${plan.popular ? 'text-white' : 'text-emerald-600'}`}>{plan.price}</h4>
              <p className={`text-sm mb-2 ${plan.popular ? 'text-emerald-100' : 'text-gray-700'}`}>{plan.hours}</p>
              <p className={`mb-4 ${plan.popular ? 'text-emerald-100' : 'text-gray-700'}`}>{plan.description}</p>
              <div className="mt-8 space-y-4 mb-4 text-left">
                {plan.features.map((feature, fidx) => (
                  <div className="flex items-center" key={fidx}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-5 h-5 ${plan.popular ? 'text-emerald-200' : 'text-emerald-600'}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className={`ml-4 ${plan.popular ? 'text-emerald-100' : 'text-gray-800'}`}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => plan.id === "Free" ? handleStartTrial() : handleSubscribe()}
              disabled={loading || (plan.id === "Free" && trialStatus && !trialStatus.eligible)}
              className={`mt-auto w-full px-4 py-2 my-8 font-medium tracking-wide capitalize transition-colors duration-300 transform rounded-2xl
                ${loading || (plan.id === "Free" && trialStatus && !trialStatus.eligible)
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                  : plan.popular ? 'bg-cream-100 text-emerald-700 hover:bg-cream-200' : 'bg-emerald-600 text-white hover:bg-emerald-700'}
              `}
            >
              {loading && plan.id === "Free" ? t("home.pricing.startingTrial", "Starting Trial...") :
               plan.id === "Free" && trialStatus && !trialStatus.eligible ?
               t("home.pricing.trialUsed", "Trial Used") : plan.cta}
            </button>
          </div>
        ))}
      </div>

      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-cream-50 p-6 rounded-xl shadow-lg text-center max-w-md w-full relative border border-emerald-200">
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="absolute top-3 right-4 text-gray-500 hover:text-gray-900 text-xl"
            >
              &times;
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t("home.pricing.loginToContinue", "Please log in to continue")}</h2>
            <button
              onClick={() => {
                setShowLoginPrompt(false);
                router.push("/login");
              }}
              className="bg-emerald-600 text-white px-6 py-2 rounded hover:bg-emerald-700"
            >
              {t("home.pricing.login", "Login")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
